/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {createContext, useEffect, useMemo, useReducer, useRef, useState, useCallback} from "react"
import { useLogout } from "@/hooks/useLogout";
import {API_URL} from "../constants"

// Types

export interface user
{
   email : string,
   admin: boolean,
   worker: boolean,
   uid: string,
   userRole : string
}

interface base {
   user : user | null
   authIsReady: boolean
   isWorker: boolean
   sessionTime : number
   justLoggedIn?: boolean
}

interface Props {
   children: JSX.Element
}

interface Action {
   type: string
   payload: any
}

// initial state for the useReducer hook
const initialState: base = {
   user: null,
   authIsReady: false,
   isWorker: false,
   sessionTime : 0
}

// create the AuthContext
export const AuthContext = createContext({} as any)

/**The reducer is a function that takes the current, state, a type and a action to change the initialState
 * is used to manage more complex state changes in a single function.*/

export const AuthReducer = (state: base, action: Action) => {
   switch (action.type) {
      case "LOGIN":
         return {...state, user: action.payload.user, isWorker: true, sessionTime : action.payload.sessionTime,  justLoggedIn: true}
      case "LOGOUT":
         return {...state, user: null, isWorker: false , sessionTime : 0,  justLoggedIn: false}
      case "IS_AUTH":
         if (!state.user || !action.payload) {
         return { ...state, user: action.payload, authIsReady: true, justLoggedIn: false }
         }
         return {
         ...state,
         user: {
            ...state.user,
            email: action.payload.email,
            worker: action.payload.worker,
            admin: action.payload.admin,
            userRole : action.payload.role
         },
         authIsReady: true,
         justLoggedIn: false
         }
      case "REFRESH_SESSION":
         return {...state,sessionTime: action.payload.sessionTime ,   justLoggedIn: false}
      case "IS_WORKER":
         return {...state, isWorker: true}
      default:
         return {...state}
   }
}

// Create the component in which we will wrap all of our app
// to provide the auth context
export const AuthContextProvider = ({children}: Props) => {

   const [state, dispatch] = useReducer(AuthReducer, initialState)
   const intervalRef = useRef<NodeJS.Timeout | null>(null)
   const {logout} = useLogout()
   const isCheckingSession = useRef(false)
   const isRefreshingSession = useRef(false)
   const fiveMinAlertedForSessionTime = useRef<number | null>(null)
   const [sessionUi, setSessionUi] = useState({
      expiringSoon: false,
      minutesLeft: null as number | null,
      refreshInProgress: false,
   })

   //function to verify the refresh token and update session
   const verifySessionRefresh = useCallback(async () => {
      try {

         if(state.user === null) return
         if (isRefreshingSession.current) return
         isRefreshingSession.current = true
         setSessionUi((prev) => ({ ...prev, refreshInProgress: true }))

         const response = await fetch(`${API_URL}/usuarios/refresh`, {
            method: "POST",
            mode: "cors",
            headers: {
               "Content-Type": "application/json",
               Origin: window.location.origin
            },
            credentials: "include",
            body: JSON.stringify({uid : state.user.uid})
         })

         if (response.status === 200) {
            const parsed = await response.json().catch(() => null)
            const sessionTtlMs =
               parsed?.data?.sessionTime ??
               parsed?.sessionTime ??
               (2 * 24 * 60 * 60 * 1000)

            // Refresh found and valid
            dispatch({type: "REFRESH_SESSION", payload: {
                  // `sessionTime` is stored as an absolute timestamp (ms since epoch)
                  sessionTime: Date.now() + sessionTtlMs
               }
            })

         } else if(response.status === 401) {
            console.error("Token invalido o expirado:", response.status)
            logout()
         }
      } catch (error) {
         console.error("Error al refrescar la sesion:", error)
         logout()
      } finally {
         isRefreshingSession.current = false
         setSessionUi((prev) => ({ ...prev, refreshInProgress: false }))
      }
   }, [logout, state.user])

   // Callback function to trigger and analyze if we need to refresh the session by time
   const checkIfNeedsRefresh = useCallback(() => {
      const now = Date.now()
      const timeUntilExpiration = state.sessionTime - now
      const thirtyMinutesInMs = 30 * 60 * 1000

      if (!state.user || state.sessionTime <= 0) {
         setSessionUi((prev) => (prev.expiringSoon || prev.minutesLeft !== null ? { ...prev, expiringSoon: false, minutesLeft: null } : prev))
         return
      }

      const minutesLeft = Math.max(0, Math.ceil(timeUntilExpiration / (60 * 1000)))
      const expiringSoon = timeUntilExpiration > 0 && timeUntilExpiration < thirtyMinutesInMs
      setSessionUi((prev) => (prev.expiringSoon === expiringSoon && prev.minutesLeft === minutesLeft ? prev : { ...prev, expiringSoon, minutesLeft }))

      //If user is active and about to expire session in less than 30 minutes, we call our function
      if (timeUntilExpiration > 0 && timeUntilExpiration < thirtyMinutesInMs) {
         console.log("Session expiring soon, refreshing...")
         verifySessionRefresh()
      } 
      //If user session already expire we call our function 
      else if (timeUntilExpiration <= 0 && state.user) {
         console.log("Session expired, attempting refresh...")
         verifySessionRefresh()
      }
   }, [state.sessionTime, state.user, verifySessionRefresh])

   //Check if user has session cookies (Effect on cookies and uid)
   useEffect(() => {
        if (isCheckingSession.current) {
         return
      }

      async function checkSession() {
         console.log("Checking session...")
         isCheckingSession.current = true
         try
         {

            const response = await fetch(`${API_URL}/usuarios/session`, {
               method: "GET",
               mode: "cors",
               headers: {
                  "Content-Type": "application/json",
                  Origin: window.location.origin,
               },
               credentials: "include",
            })
            if (response.status === 200) {
               const user = await response.json()
               if (user.data.admin || user.data.worker) 
               {
                  dispatch({type: "IS_AUTH", payload: user.data})
               }
               else 
               {
                  dispatch({type: "IS_AUTH", payload: null})
               }
            
            } 
            else 
            {
                  await verifySessionRefresh()
                  dispatch({type: "IS_AUTH", payload: null})
            }
         }
         catch (error) 
         {
            console.error("Error checking session:", error)
            dispatch({type: "IS_AUTH", payload: null})
         }
         finally
         {
            isCheckingSession.current = false
         }
      }
      checkSession()
   }, [])

  //If user is auth and we have a valid sesson time , we check every 15 minutes the status of the session
   useEffect(() => {
      if (state.user && state.sessionTime > 0) {
         checkIfNeedsRefresh()
         intervalRef.current = setInterval(checkIfNeedsRefresh , 15 * 60 * 1000) 
      }

      return () => {
         if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
         }
      }
   }, [checkIfNeedsRefresh, state.sessionTime, state.user])

   // Re-check session when user returns to the tab/window (timers can be throttled in background tabs)
   useEffect(() => {
      if (!state.user) return

      const onFocus = () => checkIfNeedsRefresh()
      const onVisibilityChange = () => {
         if (document.visibilityState === "visible") checkIfNeedsRefresh()
      }

      window.addEventListener("focus", onFocus)
      document.addEventListener("visibilitychange", onVisibilityChange)

      return () => {
         window.removeEventListener("focus", onFocus)
         document.removeEventListener("visibilitychange", onVisibilityChange)
      }
   }, [checkIfNeedsRefresh, state.user])

   useEffect(() => {
      if (!state.user) {
         setSessionUi({ expiringSoon: false, minutesLeft: null, refreshInProgress: false })
      }
   }, [state.user])

   // Simple UX: warn once when 5 minutes remain
   useEffect(() => {
      if (!state.user || state.sessionTime <= 0) return
      if (sessionUi.minutesLeft === null) return

      const shouldAlert = sessionUi.minutesLeft > 0 && sessionUi.minutesLeft <= 5
      if (!shouldAlert) return

      if (fiveMinAlertedForSessionTime.current === state.sessionTime) return
      fiveMinAlertedForSessionTime.current = state.sessionTime
      window.alert(`Your session will expire in about ${sessionUi.minutesLeft} minute(s). Please save your work.`)
   }, [sessionUi.minutesLeft, state.sessionTime, state.user])

   const contextValue = useMemo(
      () => ({ ...state, dispatch, sessionUi }),
      [state, sessionUi]
   )


   return (
      <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
   )
}
